// AssetsLiabilities.js — v7.4.0
// -----------------------------------------------------------------------------------
// ✅ Same PropertyStep look
// ✅ NO default assets shown on load (user adds via + Add Asset)
// ✅ Thousands separators for ALL $ fields (assets + liabilities)
// ✅ Robust prefill (works for crmData.assetsLiabilities OR Mercury import object)
// ✅ Never overwrites user edits after prefill
// ✅ Commits to parent on every USER change
// ✅ FIX: After Mercury mobile search/hydrate, ignore repeated external sync while focused/touched (prevents “shake”)
// ✅ Footer buttons (Back / Next)
// ✅ NEW: Home Loan liabilities link dropdown (OTHER properties only)
// ✅ NEW: Blocks Next until ALL non-subject Home Loan liabilities are linked
// ✅ NEW: HIDE SUBJECT home loan from Liabilities (refi subject loan is already captured in PropertyStep)
// ✅ NEW: If Mercury prefill contains the subject mortgage, we auto-skip it to avoid confusing duplicates
// ✅ NEW: Extra fields for OTHER-property Home Loans: Repayment + Remaining term (years)

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react"

function AssetsLiabilities({
  data = { assets: [], liabilities: [] },
  updateCrmData,

  // ✅ pass crmData.property so we can link liabilities to addresses + detect refi subject loan
  property = {},

  // ✅ Support BOTH prop conventions (BrokerCRM uses onBack/onNext; some steps use back/next)
  onNext,
  onBack,
  next,
  back,
}) {
  const goNext = onNext || next
  const goBack = onBack || back

  // -----------------------------
  // Defaults
  // -----------------------------
  const ASSET_TYPE_OPTIONS = ["Home and contents", "Shares", "Savings", "Motor vehicle", "Superannuation", "Other"]

  const emptyAsset = () => ({ type: "", value: "" })

  const emptyLiability = () => ({
    applicant: 1,
    type: "",
    creditor: "",
    limit: "",
    balance: "",
    interestRate: "",
    repayment: "",
    frequency: "monthly",

    // ✅ NEW: link to a property (OTHER properties only)
    propertyKey: "",
    propertyAddress: "",

    // ✅ NEW: remaining term for other-property loans
    remainingTermYears: "",

    // internal meta (hidden subject loan marker, etc.)
    metaKey: "",
  })

  // -----------------------------
  // Helpers
  // -----------------------------
  const digitsOnly = (v) => String(v ?? "").replace(/[^\d]/g, "")

  const formatThousands = (v) => {
    const raw = digitsOnly(v)
    if (!raw) return ""
    return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }

  const normaliseAddr = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/,/g, "")
      .trim()

  const markTouched = () => {
    touchedRef.current = true
  }

  // -----------------------------
  // Property linking options
  // -----------------------------
  const buildPropertyOptions = (prop) => {
    // IMPORTANT: We only want OTHER properties here.
    // Subject property is already captured in PropertyStep and is intentionally hidden from liabilities.
    const opts = []

    const others = Array.isArray(prop?.otherProperties) ? prop.otherProperties : []
    others.forEach((p, idx) => {
      const label = p?.address?.formatted
      if (label) opts.push({ key: `other_${idx}`, label })
    })

    return opts
  }

  const propertyOptions = useMemo(() => buildPropertyOptions(property), [property])

  // -----------------------------
  // Detect "subject refi loan already captured in PropertyStep"
  // -----------------------------
  const subjectAddr = property?.propertyAddress?.formatted || ""
  const subjectAddrNorm = useMemo(() => normaliseAddr(subjectAddr), [subjectAddr])

  const isRefinance = String(property?.applicationType || "").toLowerCase() === "refinance"

  // If they entered refi details, we treat the subject home loan as already captured.
  const subjectLoanCaptured = useMemo(() => {
    if (!isRefinance) return false
    const hasBalance = Number(digitsOnly(property?.loanBalance || "")) > 0
    const hasRate = Number(String(property?.interestRate || "").replace(/[^\d.]/g, "")) > 0
    const hasLender = String(property?.lender || property?.creditor || property?.bank || "").trim().length > 0
    return hasBalance || hasRate || hasLender
  }, [isRefinance, property?.loanBalance, property?.interestRate, property?.lender, property?.creditor, property?.bank])

  // -----------------------------
  // Prefill (Mercury import)
  // -----------------------------
  function buildPrefill(src) {
    const all = []

    const mortgages = Array.isArray(src.mortgageLoans) ? src.mortgageLoans : []
    const mortgageCount = mortgages.length

    mortgages.forEach((l) => {
      const incomingAddr = l.propertyAddress || l.securityAddress || l.address || ""
      const incomingNorm = normaliseAddr(incomingAddr)

      // ✅ If this looks like the SUBJECT mortgage, skip it (we already captured it in PropertyStep).
      // Heuristics:
      //  1) If the incoming address matches subject address -> skip
      //  2) If refi subject loan captured AND there is only 1 mortgage AND no address -> assume it’s subject -> skip
      const looksLikeSubject =
        (incomingNorm && subjectAddrNorm && incomingNorm === subjectAddrNorm) ||
        (subjectLoanCaptured && mortgageCount === 1 && !incomingNorm)

      if (looksLikeSubject) return

      all.push({
        applicant: 1,
        type: "Home Loan",
        creditor: l.creditor || "",
        limit: l.limit || "",
        balance: l.balance || "",
        interestRate: l.interestRate || "",
        repayment: l.repayment || "",
        frequency: l.repaymentFrequency || "monthly",

        propertyAddress: incomingAddr,
        propertyKey: "",

        remainingTermYears: l.remainingTermYears || l.remainingTerm || "",

        metaKey: "",
      })
    })

    ;(src.creditCards || []).forEach((l) =>
      all.push({
        applicant: 1,
        type: "Credit Card",
        creditor: l.creditor || "",
        limit: l.limit || "",
        balance: l.balance || "",
        interestRate: l.interestRate || "",
        repayment: "",
        frequency: "monthly",
        propertyKey: "",
        propertyAddress: "",
        remainingTermYears: "",
        metaKey: "",
      })
    )

    ;(src.personalLoans || []).forEach((l) =>
      all.push({
        applicant: 1,
        type: "Personal Loan",
        creditor: l.creditor || "",
        limit: l.limit || "",
        balance: l.balance || "",
        repayment: l.repayment || "",
        interestRate: l.interestRate || "",
        frequency: l.repaymentFrequency || "monthly",
        propertyKey: "",
        propertyAddress: "",
        remainingTermYears: l.remainingTermYears || l.remainingTerm || "",
        metaKey: "",
      })
    )

    ;(src.carLoans || []).forEach((l) =>
      all.push({
        applicant: 1,
        type: "Car Loan",
        creditor: l.creditor || "",
        limit: l.limit || "",
        balance: l.balance || "",
        repayment: l.repayment || "",
        interestRate: l.interestRate || "",
        frequency: l.repaymentFrequency || "monthly",
        propertyKey: "",
        propertyAddress: "",
        remainingTermYears: l.remainingTermYears || l.remainingTerm || "",
        metaKey: "",
      })
    )

    ;(src.hecs || []).forEach((l) =>
      all.push({
        applicant: 1,
        type: "HECS/HELP Debt",
        creditor: l.creditor || "",
        limit: "",
        balance: l.balance || "",
        interestRate: "",
        repayment: "",
        frequency: "monthly",
        propertyKey: "",
        propertyAddress: "",
        remainingTermYears: "",
        metaKey: "",
      })
    )

    return all
  }

  // -----------------------------
  // Normalise incoming data shape
  // -----------------------------
  const getPrefillSource = (d) => {
    if (!d || typeof d !== "object") return {}
    if (d.mortgageLoans || d.creditCards || d.personalLoans || d.carLoans || d.hecs) return d
    if (
      d.prefill &&
      (d.prefill.mortgageLoans || d.prefill.creditCards || d.prefill.personalLoans || d.prefill.carLoans || d.prefill.hecs)
    ) {
      return d.prefill
    }
    return {}
  }

  const hasAnyPrefillArrays = (src) =>
    !!(
      (Array.isArray(src.mortgageLoans) && src.mortgageLoans.length) ||
      (Array.isArray(src.creditCards) && src.creditCards.length) ||
      (Array.isArray(src.personalLoans) && src.personalLoans.length) ||
      (Array.isArray(src.carLoans) && src.carLoans.length) ||
      (Array.isArray(src.hecs) && src.hecs.length)
    )

  // -----------------------------
  // Local State
  // -----------------------------
  const [liabilities, setLiabilities] = useState(() =>
    Array.isArray(data.liabilities) && data.liabilities.length > 0 ? data.liabilities : [emptyLiability()]
  )
  const [assets, setAssets] = useState(() => (Array.isArray(data.assets) && data.assets.length > 0 ? data.assets : []))

  // Prefill only once
  const didPrefillRef = useRef(false)

  // Track if user has interacted (to stop external hydration “bouncing” the UI)
  const touchedRef = useRef(false)

  // Track focus inside this component (if parent keeps hydrating while focused → shake)
  const isFocusedRef = useRef(false)

  // Keep latest state in refs (avoids stale closure)
  const assetsRef = useRef(assets)
  const liabsRef = useRef(liabilities)
  useEffect(() => {
    assetsRef.current = assets
  }, [assets])
  useEffect(() => {
    liabsRef.current = liabilities
  }, [liabilities])

  // -----------------------------
  // Commit helper (USER changes)
  // -----------------------------
  const commit = useCallback(
    (nextAssets, nextLiabs) => {
      if (typeof updateCrmData === "function") {
        updateCrmData({ assets: nextAssets, liabilities: nextLiabs })
      }
    },
    [updateCrmData]
  )

  // -----------------------------
  // Utility: remove/skip subject home loan rows (if any ever exist)
  // -----------------------------
  const stripSubjectHomeLoans = useCallback(
    (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return arr

      if (!subjectLoanCaptured) return arr

      const subjNorm = subjectAddrNorm
      const isSubjectRow = (l) => {
        if (String(l?.metaKey || "") === "subject_home_loan") return true
        if (String(l?.propertyKey || "") === "subject") return true
        const addrNorm = normaliseAddr(l?.propertyAddress || "")
        if (addrNorm && subjNorm && addrNorm === subjNorm) return true
        // If row has no property link/address and it's a Home Loan, we treat it as "potentially subject"
        // BUT only strip if there are no OTHER-property options (so we don’t accidentally delete investment loans).
        if (l?.type === "Home Loan" && !addrNorm && !String(l?.propertyKey || "").trim()) {
          // keep it if user has other properties available (it might be an investment loan needing link)
          if (propertyOptions.length > 0) return false
          return true
        }
        return false
      }

      return arr.filter((l) => !(l?.type === "Home Loan" && isSubjectRow(l)))
    },
    [subjectLoanCaptured, subjectAddrNorm, propertyOptions.length]
  )

  // -----------------------------
  // Robust prefill ONCE (never overwrites user edits)
  // -----------------------------
  useEffect(() => {
    if (didPrefillRef.current) return

    // If liabilities already exist (from CRM), do nothing
    if (Array.isArray(data.liabilities) && data.liabilities.length > 0) {
      didPrefillRef.current = true
      return
    }

    const src = getPrefillSource(data)
    if (!hasAnyPrefillArrays(src)) return

    const prefilled = buildPrefill(src)
    const cleaned = stripSubjectHomeLoans(prefilled)

    if (cleaned.length > 0) {
      setLiabilities(cleaned)
      commit(assetsRef.current, cleaned) // commit once for prefill
      didPrefillRef.current = true
    } else {
      // if everything was subject + stripped, fall back to a single empty row
      setLiabilities([emptyLiability()])
      commit(assetsRef.current, [emptyLiability()])
      didPrefillRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, commit, stripSubjectHomeLoans])

  // -----------------------------
  // External sync (Mercury hydrate / autosave restore)
  // FIX: If parent keeps rehydrating repeatedly, ignore updates while focused or after user touched.
  // Also: accept the FIRST external hydrate, then stop “bouncing” the UI.
  // -----------------------------
  const didExternalHydrateRef = useRef(false)

  const stableStringify = (obj) => {
    try {
      return JSON.stringify(obj ?? null)
    } catch {
      return ""
    }
  }

  const lastExternalJSONRef = useRef({
    assetsJSON: stableStringify(Array.isArray(data.assets) ? data.assets : []),
    liabsJSON: stableStringify(Array.isArray(data.liabilities) ? data.liabilities : []),
  })

  useEffect(() => {
    const extAssets = Array.isArray(data.assets) ? data.assets : []
    const extLiabsRaw = Array.isArray(data.liabilities) ? data.liabilities : []

    // ✅ always strip subject home loan duplicates coming in from outside
    const extLiabs = stripSubjectHomeLoans(extLiabsRaw)

    const nextAssetsJSON = stableStringify(extAssets)
    const nextLiabsJSON = stableStringify(extLiabs)

    const prev = lastExternalJSONRef.current
    const assetsChanged = prev.assetsJSON !== nextAssetsJSON
    const liabsChanged = prev.liabsJSON !== nextLiabsJSON

    if (!assetsChanged && !liabsChanged) return

    // If user is interacting, do not apply external “bounce” updates
    if (isFocusedRef.current) return
    if (touchedRef.current && didExternalHydrateRef.current) return

    lastExternalJSONRef.current = { assetsJSON: nextAssetsJSON, liabsJSON: nextLiabsJSON }

    if (assetsChanged) setAssets(extAssets)

    if (liabsChanged) {
      if (extLiabs.length > 0) {
        setLiabilities(extLiabs)
        didPrefillRef.current = true
      } else {
        setLiabilities([emptyLiability()])
      }
    }

    if ((extAssets && extAssets.length) || (extLiabs && extLiabs.length)) {
      didExternalHydrateRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.assets, data.liabilities, stripSubjectHomeLoans])

  // -----------------------------
  // Mutators (commit immediately)
  // -----------------------------
  const updateLiability = (i, field, value) => {
    markTouched()
    setLiabilities((prev) => {
      const next = [...prev]
      next[i] = { ...next[i], [field]: value }
      const cleaned = stripSubjectHomeLoans(next)
      commit(assetsRef.current, cleaned)
      return cleaned
    })
  }

  const patchLiability = (i, patch) => {
    markTouched()
    setLiabilities((prev) => {
      const next = [...prev]
      next[i] = { ...next[i], ...patch }
      const cleaned = stripSubjectHomeLoans(next)
      commit(assetsRef.current, cleaned)
      return cleaned
    })
  }

  const addLiability = () => {
    markTouched()
    setLiabilities((prev) => {
      const next = [...prev, emptyLiability()]
      const cleaned = stripSubjectHomeLoans(next)
      commit(assetsRef.current, cleaned)
      return cleaned
    })
  }

  const removeLiability = (index) => {
    markTouched()
    setLiabilities((prev) => {
      const next = prev.filter((_, i) => i !== index)
      const safeNext = next.length ? next : [emptyLiability()]
      const cleaned = stripSubjectHomeLoans(safeNext)
      commit(assetsRef.current, cleaned)
      return cleaned
    })
  }

  const updateAsset = (i, field, value) => {
    markTouched()
    setAssets((prev) => {
      const next = [...prev]
      next[i] = { ...next[i], [field]: value }
      commit(next, liabsRef.current)
      return next
    })
  }

  const addAsset = () => {
    markTouched()
    setAssets((prev) => {
      const next = [...prev, emptyAsset()]
      commit(next, liabsRef.current)
      return next
    })
  }

  const removeAsset = (index) => {
    markTouched()
    setAssets((prev) => {
      const next = prev.filter((_, i) => i !== index)
      commit(next, liabsRef.current)
      return next
    })
  }

  // -----------------------------
  // REQUIRED: Home Loan must be linked (NON-subject only)
  // -----------------------------
  const actionableLiabilities = useMemo(() => {
    // what we actually render/validate
    const arr = Array.isArray(liabilities) ? liabilities : []
    return stripSubjectHomeLoans(arr)
  }, [liabilities, stripSubjectHomeLoans])

  const homeLoansMissingLink = useMemo(() => {
    const arr = Array.isArray(actionableLiabilities) ? actionableLiabilities : []
    return arr.filter((l) => l?.type === "Home Loan" && !String(l?.propertyKey || "").trim())
  }, [actionableLiabilities])

  const canProceed = homeLoansMissingLink.length === 0

  const handleNextClick = () => {
    if (!canProceed) return
    if (typeof goNext === "function") goNext()
  }

  // -----------------------------
  // UI (PropertyStep look)
  // -----------------------------
  const ui = useMemo(() => {
    const ACCENT = "#4EDCF6"
    const DARK = "#111827"
    const MUTED = "#6B7280"
    const BORDER = "#E5E7EB"
    const INPUT_BORDER = "#334155"
    const CARD_BG = "#E6FBFF"
    const SHADOW = "0 10px 24px rgba(78, 220, 246, 0.22)"

    const page = { padding: 8, maxWidth: 920 }
    const h2 = { marginBottom: 12, fontSize: 26, fontWeight: 700, color: DARK }
    const sectionTitle = { margin: 0, marginBottom: 10, fontSize: 20, fontWeight: 700, color: DARK }

    const card = {
      background: CARD_BG,
      padding: 18,
      borderRadius: 18,
      border: `3px solid ${ACCENT}`,
      boxShadow: SHADOW,
      marginBottom: 14,
    }

    const subCard = {
      background: "#fff",
      padding: 14,
      borderRadius: 16,
      border: `2px solid ${BORDER}`,
      marginTop: 12,
    }

    const label = { display: "block", marginBottom: 6, fontWeight: 600, color: DARK, fontSize: 15 }
    const helper = { color: MUTED, fontSize: 13, fontWeight: 500, marginTop: 10 }

    const grid3 = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, alignItems: "end" }
    const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, alignItems: "end" }

    const input = {
      width: "100%",
      height: 50,
      padding: "10px 12px",
      borderRadius: 14,
      border: `2px solid ${INPUT_BORDER}`,
      background: "#fff",
      fontSize: 16,
      fontWeight: 600,
      outline: "none",
      boxShadow: "none",
      minWidth: 0,
    }

    const adornWrap = { position: "relative", width: "100%", height: 50 }
    const adornLeft = {
      position: "absolute",
      left: 14,
      top: "50%",
      transform: "translateY(-50%)",
      fontSize: 16,
      fontWeight: 700,
      color: DARK,
      opacity: 0.8,
      pointerEvents: "none",
      lineHeight: 1,
      zIndex: 2,
    }
    const inputWithLeftAdorn = { ...input, paddingLeft: 34 }

    const btnPrimary = {
      padding: "12px 18px",
      background: ACCENT,
      border: "none",
      borderRadius: 12,
      cursor: "pointer",
      fontWeight: 650,
      boxShadow: SHADOW,
    }

    const removeBtn = {
      border: "none",
      background: "transparent",
      fontSize: 16,
      color: "#DC2626",
      cursor: "pointer",
      fontWeight: 650,
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      height: 50,
    }

    const emptyState = {
      padding: "14px",
      borderRadius: 14,
      border: `2px dashed ${BORDER}`,
      background: "#FAFAFA",
      color: MUTED,
      fontWeight: 600,
      marginBottom: 12,
    }

    const footerRow = { display: "flex", justifyContent: "space-between", gap: 16, marginTop: 22 }
    const btnBack = {
      flex: 1,
      padding: "18px 16px",
      borderRadius: 16,
      border: "1px solid rgba(20,20,20,0.12)",
      background: "#fff",
      fontWeight: 800,
      cursor: "pointer",
    }
    const btnNext = {
      flex: 1,
      padding: "18px 16px",
      borderRadius: 16,
      border: "none",
      background: ACCENT,
      color: "#141414",
      fontWeight: 900,
      cursor: "pointer",
      boxShadow: SHADOW,
    }
    const btnNextDisabled = {
      ...btnNext,
      opacity: 0.55,
      cursor: "not-allowed",
      boxShadow: "none",
    }

    const warningBadge = {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 12px",
      borderRadius: 14,
      border: "1px solid rgba(245,158,11,0.35)",
      background: "rgba(245,158,11,0.12)",
      color: "rgba(20,20,20,0.85)",
      fontWeight: 750,
      marginTop: 10,
    }

    const infoBadge = {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 12px",
      borderRadius: 14,
      border: "1px solid rgba(59,130,246,0.25)",
      background: "rgba(59,130,246,0.08)",
      color: "rgba(20,20,20,0.82)",
      fontWeight: 750,
      marginTop: 10,
    }

    return {
      page,
      h2,
      sectionTitle,
      card,
      subCard,
      label,
      helper,
      grid3,
      grid2,
      input,
      adornWrap,
      adornLeft,
      inputWithLeftAdorn,
      btnPrimary,
      removeBtn,
      emptyState,
      footerRow,
      btnBack,
      btnNext,
      btnNextDisabled,
      warningBadge,
      infoBadge,
    }
  }, [])

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <div
      style={ui.page}
      onFocusCapture={() => {
        isFocusedRef.current = true
      }}
      onBlurCapture={() => {
        isFocusedRef.current = false
      }}
    >
      <h2 style={ui.h2}>Assets &amp; Liabilities</h2>

      {/* ---------------- ASSETS ---------------- */}
      <div style={ui.card}>
        <h3 style={ui.sectionTitle}>Assets</h3>

        <div style={ui.subCard}>
          {assets.length === 0 && <div style={ui.emptyState}>No assets added yet. Click “+ Add Asset” to begin.</div>}

          {assets.map((a, i) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr auto",
                gap: 14,
                alignItems: "end",
                marginBottom: 12,
              }}
            >
              <div>
                <label style={ui.label}>Asset type</label>
                <select style={ui.input} value={a.type || ""} onChange={(e) => updateAsset(i, "type", e.target.value)}>
                  <option value="">Select type</option>
                  {ASSET_TYPE_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={ui.label}>Value</label>
                <div style={ui.adornWrap}>
                  <span style={ui.adornLeft}>$</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="0"
                    value={formatThousands(a.value ?? "")}
                    onChange={(e) => updateAsset(i, "value", digitsOnly(e.target.value))}
                    style={ui.inputWithLeftAdorn}
                  />
                </div>
              </div>

              <button type="button" onClick={() => removeAsset(i)} style={ui.removeBtn}>
                ❌ Remove
              </button>
            </div>
          ))}

          <button type="button" style={ui.btnPrimary} onClick={addAsset}>
            + Add Asset
          </button>

          <div style={ui.helper}>Leave blank values if unknown — we’ll refine these during assessment.</div>
        </div>
      </div>

      {/* ---------------- LIABILITIES ---------------- */}
      <div style={ui.card}>
        <h3 style={ui.sectionTitle}>Liabilities</h3>

        <div style={ui.subCard}>
          {/* ✅ Helpful note: why subject refi loan isn't shown here */}
          {subjectLoanCaptured && (
            <div style={ui.infoBadge}>
              <span style={{ fontSize: 16 }}>ℹ️</span>
              <span>
                Your <strong>main home loan</strong> on the <strong>subject property</strong> is captured in the Property step, so we don’t show it
                again here. Add any <strong>other loans</strong> (e.g. investment properties, credit cards, personal loans) below.
              </span>
            </div>
          )}

          {actionableLiabilities.map((l, i) => {
            const isHomeLoan = l.type === "Home Loan"
            const needsLink = isHomeLoan && !String(l.propertyKey || "").trim()

            return (
              <div
                key={i}
                style={{
                  padding: 14,
                  borderRadius: 16,
                  border: "2px solid #E5E7EB",
                  background: "#fff",
                  marginBottom: 14,
                }}
              >
                {/* ROW 1 */}
                <div style={ui.grid3}>
                  <div>
                    <label style={ui.label}>Applicant</label>
                    <select style={ui.input} value={l.applicant} onChange={(e) => updateLiability(i, "applicant", Number(e.target.value))}>
                      <option value={1}>Applicant 1</option>
                      <option value={2}>Applicant 2</option>
                    </select>
                  </div>

                  <div>
                    <label style={ui.label}>Liability type</label>
                    <select
                      style={ui.input}
                      value={l.type}
                      onChange={(e) => {
                        const nextType = e.target.value

                        // If switching away from Home Loan, clear link fields
                        if (nextType !== "Home Loan") {
                          patchLiability(i, {
                            type: nextType,
                            propertyKey: "",
                            propertyAddress: "",
                            remainingTermYears: "",
                          })
                        } else {
                          patchLiability(i, { type: nextType })
                        }
                      }}
                    >
                      <option value="">Select type</option>
                      <option value="Home Loan">Home Loan</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Personal Loan">Personal Loan</option>
                      <option value="Car Loan">Car Loan</option>
                      <option value="HECS/HELP Debt">HECS/HELP Debt</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label style={ui.label}>Creditor</label>
                    <input
                      style={ui.input}
                      placeholder="Creditor"
                      value={l.creditor}
                      onChange={(e) => updateLiability(i, "creditor", e.target.value)}
                    />
                  </div>
                </div>

                {/* ✅ LINKED PROPERTY (required for Home Loan) — ONLY other properties */}
                {isHomeLoan && (
                  <div style={{ marginTop: 12 }}>
                    <label style={ui.label}>Linked property (required)</label>

                    <select
                      style={ui.input}
                      value={l.propertyKey || ""}
                      onChange={(e) => {
                        const key = e.target.value
                        const found = propertyOptions.find((p) => p.key === key)
                        patchLiability(i, {
                          propertyKey: key,
                          propertyAddress: found?.label || "",
                        })
                      }}
                    >
                      <option value="">Select property</option>
                      {propertyOptions.map((p) => (
                        <option key={p.key} value={p.key}>
                          {p.label}
                        </option>
                      ))}
                    </select>

                    {needsLink && (
                      <div style={ui.warningBadge}>
                        <span style={{ fontSize: 16 }}>⚠️</span>
                        <span>Please link this Home Loan to an (other) property address to continue.</span>
                      </div>
                    )}

                    {!!l.propertyAddress && !needsLink && (
                      <div style={{ marginTop: 10, fontWeight: 750, opacity: 0.85 }}>Linked to: {l.propertyAddress}</div>
                    )}

                    {propertyOptions.length === 0 && (
                      <div style={{ marginTop: 10, opacity: 0.75, fontWeight: 650 }}>
                        No other properties found to link. Add “Do you own any other property?” addresses first.
                      </div>
                    )}
                  </div>
                )}

                {/* ROW 2 – changes by type */}
                {l.type === "Home Loan" && (
                  <div style={{ marginTop: 12 }}>
                    <div style={ui.grid3}>
                      <div>
                        <label style={ui.label}>Limit</label>
                        <div style={ui.adornWrap}>
                          <span style={ui.adornLeft}>$</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="0"
                            value={formatThousands(l.limit)}
                            onChange={(e) => updateLiability(i, "limit", digitsOnly(e.target.value))}
                            style={ui.inputWithLeftAdorn}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={ui.label}>Balance</label>
                        <div style={ui.adornWrap}>
                          <span style={ui.adornLeft}>$</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="0"
                            value={formatThousands(l.balance)}
                            onChange={(e) => updateLiability(i, "balance", digitsOnly(e.target.value))}
                            style={ui.inputWithLeftAdorn}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={ui.label}>Interest rate (%)</label>
                        <input
                          style={ui.input}
                          inputMode="decimal"
                          placeholder="0.00"
                          value={l.interestRate}
                          onChange={(e) => updateLiability(i, "interestRate", String(e.target.value).replace(/[^\d.]/g, ""))}
                        />
                      </div>
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <div style={ui.grid3}>
                        <div>
                          <label style={ui.label}>Repayment</label>
                          <div style={ui.adornWrap}>
                            <span style={ui.adornLeft}>$</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              placeholder="0"
                              value={formatThousands(l.repayment)}
                              onChange={(e) => updateLiability(i, "repayment", digitsOnly(e.target.value))}
                              style={ui.inputWithLeftAdorn}
                            />
                          </div>
                        </div>

                        <div>
                          <label style={ui.label}>Frequency</label>
                          <select style={ui.input} value={l.frequency || "monthly"} onChange={(e) => updateLiability(i, "frequency", e.target.value)}>
                            <option value="weekly">Weekly</option>
                            <option value="fortnightly">Fortnightly</option>
                            <option value="monthly">Monthly</option>
                            <option value="annual">Annual</option>
                          </select>
                        </div>

                        <div>
                          <label style={ui.label}>Remaining term (years)</label>
                          <input
                            style={ui.input}
                            inputMode="numeric"
                            placeholder="e.g. 25"
                            value={l.remainingTermYears || ""}
                            onChange={(e) => updateLiability(i, "remainingTermYears", digitsOnly(e.target.value))}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {l.type === "Credit Card" && (
                  <div style={{ marginTop: 12 }}>
                    <div style={ui.grid2}>
                      <div>
                        <label style={ui.label}>Limit</label>
                        <div style={ui.adornWrap}>
                          <span style={ui.adornLeft}>$</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="0"
                            value={formatThousands(l.limit)}
                            onChange={(e) => updateLiability(i, "limit", digitsOnly(e.target.value))}
                            style={ui.inputWithLeftAdorn}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={ui.label}>Balance</label>
                        <div style={ui.adornWrap}>
                          <span style={ui.adornLeft}>$</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="0"
                            value={formatThousands(l.balance)}
                            onChange={(e) => updateLiability(i, "balance", digitsOnly(e.target.value))}
                            style={ui.inputWithLeftAdorn}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {(l.type === "Personal Loan" || l.type === "Car Loan") && (
                  <div style={{ marginTop: 12 }}>
                    <div style={ui.grid3}>
                      <div>
                        <label style={ui.label}>Balance</label>
                        <div style={ui.adornWrap}>
                          <span style={ui.adornLeft}>$</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="0"
                            value={formatThousands(l.balance)}
                            onChange={(e) => updateLiability(i, "balance", digitsOnly(e.target.value))}
                            style={ui.inputWithLeftAdorn}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={ui.label}>Repayment</label>
                        <div style={ui.adornWrap}>
                          <span style={ui.adornLeft}>$</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="0"
                            value={formatThousands(l.repayment)}
                            onChange={(e) => updateLiability(i, "repayment", digitsOnly(e.target.value))}
                            style={ui.inputWithLeftAdorn}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={ui.label}>Interest rate (%)</label>
                        <input
                          style={ui.input}
                          inputMode="decimal"
                          placeholder="0.00"
                          value={l.interestRate}
                          onChange={(e) => updateLiability(i, "interestRate", String(e.target.value).replace(/[^\d.]/g, ""))}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {l.type === "HECS/HELP Debt" && (
                  <div style={{ marginTop: 12 }}>
                    <div style={ui.grid2}>
                      <div>
                        <label style={ui.label}>Balance</label>
                        <div style={ui.adornWrap}>
                          <span style={ui.adornLeft}>$</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="0"
                            value={formatThousands(l.balance)}
                            onChange={(e) => updateLiability(i, "balance", digitsOnly(e.target.value))}
                            style={ui.inputWithLeftAdorn}
                          />
                        </div>
                      </div>
                      <div />
                    </div>
                  </div>
                )}

                {l.type === "Other" && (
                  <div style={{ marginTop: 12 }}>
                    <div style={ui.grid3}>
                      <div>
                        <label style={ui.label}>Balance</label>
                        <div style={ui.adornWrap}>
                          <span style={ui.adornLeft}>$</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="0"
                            value={formatThousands(l.balance)}
                            onChange={(e) => updateLiability(i, "balance", digitsOnly(e.target.value))}
                            style={ui.inputWithLeftAdorn}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={ui.label}>Repayment</label>
                        <div style={ui.adornWrap}>
                          <span style={ui.adornLeft}>$</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="0"
                            value={formatThousands(l.repayment)}
                            onChange={(e) => updateLiability(i, "repayment", digitsOnly(e.target.value))}
                            style={ui.inputWithLeftAdorn}
                          />
                        </div>
                      </div>

                      <div>
                        <label style={ui.label}>Interest rate (%)</label>
                        <input
                          style={ui.input}
                          inputMode="decimal"
                          placeholder="0.00"
                          value={l.interestRate}
                          onChange={(e) => updateLiability(i, "interestRate", String(e.target.value).replace(/[^\d.]/g, ""))}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* REMOVE */}
                <div style={{ marginTop: 12, textAlign: "right" }}>
                  <button type="button" style={{ ...ui.removeBtn, height: "auto" }} onClick={() => removeLiability(i)}>
                    ❌ Remove liability
                  </button>
                </div>
              </div>
            )
          })}

          <button type="button" style={ui.btnPrimary} onClick={addLiability}>
            + Add Liability
          </button>

          {/* Global warning if any Home Loan missing link */}
          {!canProceed && (
            <div style={ui.warningBadge}>
              <span style={{ fontSize: 16 }}>⚠️</span>
              <span>
                You have {homeLoansMissingLink.length} Home Loan{homeLoansMissingLink.length === 1 ? "" : "s"} not linked to an (other) property.
                Link them to continue.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ✅ Footer nav (Back / Next) */}
      {(goBack || goNext) && (
        <div style={ui.footerRow}>
          <button type="button" onClick={goBack} style={ui.btnBack}>
            ← Back
          </button>

          <button
            type="button"
            onClick={handleNextClick}
            style={canProceed ? ui.btnNext : ui.btnNextDisabled}
            aria-disabled={!canProceed}
            title={!canProceed ? "Link all Home Loan liabilities to an (other) property to continue" : "Next"}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  )
}

export default AssetsLiabilities